import type { JournalEntry } from '../journal/JournalEntry';
import type { AppPreferences } from '../preferences/AppPreferences';
import type { Profile } from '../profile/Profile';
import type { DailyRoutine, DailyStep, RoutineStepTemplate, RoutineTemplate } from '../routine/models';
import { DomainError } from '../shared/DomainError';
import type { ExerciseLog } from '../workout/ExerciseLog';
import type { WorkoutSession } from '../workout/WorkoutSession';

/**
 * Canonical, versioned, Dexie-independent export format (product/L0_DECISIONS_CLOSED.md §7,
 * MVP_ACCEPTANCE_CRITERIA.md AC-DATA-05). Contains every personal, profile-scoped record —
 * missions, days, journals, sport history and settings. Deliberately excludes the philosophy
 * corpus (global, non-personal, shipped app content, always restored by idempotent seeding) and
 * backup metadata (an audit trail of exports/imports themselves, not portable user data).
 */
export interface ExportEnvelope {
  readonly version: number;
  readonly exportedAt: string;
  readonly profile: Profile;
  readonly routineTemplates: readonly RoutineTemplate[];
  readonly routineStepTemplates: readonly RoutineStepTemplate[];
  readonly dailyRoutines: readonly DailyRoutine[];
  readonly dailySteps: readonly DailyStep[];
  readonly journalEntries: readonly JournalEntry[];
  readonly workoutSessions: readonly WorkoutSession[];
  readonly exerciseLogs: readonly ExerciseLog[];
  readonly appPreferences: readonly AppPreferences[];
}

export const EXPORT_ENVELOPE_VERSION = 1;

const ARRAY_KEYS = [
  'routineTemplates',
  'routineStepTemplates',
  'dailyRoutines',
  'dailySteps',
  'journalEntries',
  'workoutSessions',
  'exerciseLogs',
  'appPreferences',
] as const;

/**
 * Top-level shape and version check only (AC-L0-IMP-04: an invalid schema or version blocks the
 * import entirely). Per-record validity is enforced by the existing persistence mappers at write
 * time — a single malformed record aborts the whole transaction and nothing is written
 * (AC-DATA-08), without duplicating that validation logic here.
 */
export function isExportEnvelopeShape(value: unknown): value is ExportEnvelope {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  if (candidate.version !== EXPORT_ENVELOPE_VERSION) return false;
  if (typeof candidate.exportedAt !== 'string') return false;
  if (typeof candidate.profile !== 'object' || candidate.profile === null) return false;
  return ARRAY_KEYS.every((key) => Array.isArray(candidate[key]));
}

/** Rejects malformed JSON and an invalid/missing schema before any data is touched (AC-L0-IMP-04). */
export function parseExportEnvelope(raw: string): ExportEnvelope {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new DomainError('invalid_export_json', 'Ce fichier n’est pas un JSON valide.');
  }
  if (!isExportEnvelopeShape(parsed)) {
    throw new DomainError(
      'invalid_export_schema',
      'Ce fichier ne correspond pas au format ou à la version attendue.',
    );
  }
  return parsed;
}

export interface ImportPreview {
  readonly exportedAt: string;
  readonly missionCount: number;
  readonly dayCount: number;
  readonly journalEntryCount: number;
  readonly workoutSessionCount: number;
}

export function buildImportPreview(envelope: ExportEnvelope): ImportPreview {
  return {
    exportedAt: envelope.exportedAt,
    missionCount: envelope.dailySteps.length,
    dayCount: envelope.dailyRoutines.length,
    journalEntryCount: envelope.journalEntries.length,
    workoutSessionCount: envelope.workoutSessions.length,
  };
}
